/**
 * Reddit addresses, told apart by what they point at. Pure, so the Worker (naming a link) and the app (labelling one it
 * could not name) read an address the same way.
 */
export type RedditKind = 'answers' | 'comment' | 'other' | 'post' | 'share' | 'short' | 'subreddit' | 'user'

export interface RedditTarget {
  kind: RedditKind
  subreddit?: string
  postId?: string
  slug?: string
  commentId?: string
  user?: string
  /** The question of a Reddit Answers link (`?q=`). */
  query?: string
}

const SORTS = new Set(['hot', 'new', 'top', 'rising', 'best', 'controversial'])
const USER_TABS = new Set(['about', 'comments', 'posts', 'submitted'])
const PAGE_HOST = /^(?:www\.|old\.|new\.|np\.|m\.)?reddit\.com$/

function redditUrl(value: string): URL | null {
  try {
    const url = new URL(value)
    return PAGE_HOST.test(url.hostname.toLowerCase()) || url.hostname.toLowerCase() === 'redd.it' ? url : null
  } catch {
    return null
  }
}

export function isRedditPage(targetUrl: string): boolean {
  return redditUrl(targetUrl) !== null
}

export function classifyRedditUrl(targetUrl: string): RedditTarget | null {
  const url = redditUrl(targetUrl)
  if (!url) return null
  const parts = url.pathname.split('/').filter(Boolean)
  if (url.hostname.toLowerCase() === 'redd.it') return parts[0] && parts.length === 1 ? { kind: 'short', postId: parts[0] } : { kind: 'other' }
  const [first, second, third, fourth, fifth, sixth] = parts
  // `/a/answers/s/<id>`, `/r/<sub>/s/<id>` and `/user/<name>/s/<id>` are only a redirect to the real page.
  if (first === 'a' && second === 'answers' && third === 's') return { kind: 'share' }
  if ((first === 'r' || first === 'user' || first === 'u') && second && third === 's' && fourth) return { kind: 'share', ...(first === 'r' ? { subreddit: second } : {}) }
  if (first === 'answers') return { kind: 'answers', query: url.searchParams.get('q') ?? undefined }
  if (first === 'r' && second && third === 'comments' && fourth) {
    // `/comments/<id>/comment/<comment>/` and `/comments/<id>/<slug>/<comment>/` both name one comment.
    const slug = fifth === 'comment' ? undefined : fifth
    const commentId = sixth
    return {
      kind: commentId ? 'comment' : 'post',
      subreddit: second,
      postId: fourth,
      ...(slug ? { slug } : {}),
      ...(commentId ? { commentId } : {}),
    }
  }
  if (first === 'comments' && second) {
    return third ? { kind: 'post', postId: second, slug: third } : { kind: 'short', postId: second }
  }
  if (first === 'r' && second && (parts.length === 2 || SORTS.has(third ?? ''))) return { kind: 'subreddit', subreddit: second }
  if ((first === 'user' || first === 'u') && second && (parts.length === 2 || USER_TABS.has(third ?? ''))) return { kind: 'user', user: second }
  return { kind: 'other' }
}

/** The address with the share and tracking parameters gone. */
function postPath(target: RedditTarget): string | null {
  if (!target.postId) return null
  const sub = target.subreddit ? `/r/${target.subreddit}` : ''
  return `${sub}/comments/${target.postId}${target.slug ? `/${target.slug}` : ''}/`
}

export function oldRedditUrl(target: RedditTarget): string | null {
  const path = postPath(target)
  return path ? `https://old.reddit.com${path}${target.commentId ? `${target.slug ? '' : 'comment/'}${target.commentId}/` : ''}` : null
}

export function canonicalPostUrl(target: RedditTarget): string | null {
  const path = postPath(target)
  return path ? `https://www.reddit.com${path}` : null
}
